import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import Vue from 'vue';
import { MAX_LAYER_NODE_LENGTH, SPECIAL_NODE_NAME_LIST } from '@/constants/customField';
import { Toast } from 'patsnap-biz';
import { injectService } from '@/inversify.config';
import { i18nSymbol } from '@/i18n';
import { isValidSpecial } from '@patsnap-utils/validations/src';
export const forEachTree = (nodes, cb) => {
    nodes.forEach((node) => {
        cb(node);
        if (node.children) {
            forEachTree(node.children, cb);
        }
    });
};
export class LayerTreeService {
    get treeListData() {
        const list = [];
        forEachTree(this.treeData, (item) => {
            list.push(item);
        });
        return list;
    }
    constructor(data) {
        // 编辑相关
        this.maxTreeLevel = 6;
        this.editNode = null;
        this.beforeCreateNode = (newNode) => {
            return newNode;
        };
        // 拖拽过程相关
        this.dragOption = {
            dragNode: null,
            hoverNode: null,
            hoverType: null,
            hoverElement: null,
            defaultBeforeAfterOffset: 10,
            dragEventDelay: 80,
            dragHelperNodeClassName: 'edit-layer-tree__drag-helper-node',
        };
        this.handleDragOver = _.throttle((e, node, el) => {
            this.dragOption.hoverNode = node;
            this.dragOption.hoverElement = el;
            // Safari不支持拖拽时自动滚动，只能手动实现
            const treeContainer = this.treeRef.$el;
            const { top: _top, height: _height } = treeContainer.getBoundingClientRect();
            const _bottom = _top + _height;
            if (e.clientY - _top < 20) {
                treeContainer.scrollTop = treeContainer.scrollTop - 10;
            }
            if (_bottom - e.clientY < 20) {
                treeContainer.scrollTop = treeContainer.scrollTop + 10;
            }
            const { hoverNode, dragNode, hoverElement, defaultBeforeAfterOffset = 10, } = this.dragOption || {};
            if (!hoverNode)
                return console.warn('[No Hover Node]');
            if (!dragNode)
                return console.warn('[No Drag Node]');
            if (!hoverElement)
                return console.warn('[No Hover Element]');
            let parent = hoverNode.parent;
            while (parent) {
                if (dragNode === parent) {
                    return console.warn('[Can not drag into child]');
                }
                parent = parent.parent;
            }
            if (dragNode === hoverNode) {
                return console.warn('[Can not drag into self]');
            }
            const { top, height } = hoverElement.getBoundingClientRect();
            const offset = e.clientY - top;
            let type = null;
            if (offset < defaultBeforeAfterOffset) {
                type = 'before';
            }
            else if (offset > height - defaultBeforeAfterOffset) {
                type = 'after';
            }
            else {
                type = 'inner';
            }
            this.dragOption.hoverType = type;
        }, this.dragOption.dragEventDelay, {
            leading: true,
            trailing: false,
        });
        this.treeData = data;
        this.i18n = injectService(i18nSymbol);
    }
    setTreeRef(treeRef) {
        this.treeRef = treeRef;
    }
    updateTreeRef() {
        try {
            this.treeRef.$refs.virtualScrollerCompRef?.forceUpdate();
        }
        catch (error) {
            console.log(error);
        }
    }
    getNode(id) {
        if (!this.treeRef)
            return null;
        return this.treeRef.getNode({ key: id });
    }
    formatName(name) {
        let _name = _.trim(name) || '';
        _name = _name.replace(/[\n]/g, '');
        return _name;
    }
    startEdit(nodeData, isNew) {
        if (this.editNode) {
            this.finishEdit();
        }
        this.editNode = {
            data: nodeData,
            id: nodeData.id,
            name: nodeData.name,
            error: false,
            errorMsg: '',
            isNew,
        };
        this.focusNode(nodeData.id);
    }
    // 回车 失去焦点时候触发
    confirmEdit() {
        if (!this.editNode)
            return;
        this.validateNode(this.editNode);
        const { error, name } = this.editNode;
        // 有错误时，如果不是没填内容的错误，失焦不会取消编辑状态，让错误常驻，如果是没填内容的错误，失焦会取消编辑状态
        if (error && this.formatName(name))
            return;
        this.finishEdit();
    }
    finishEdit() {
        if (!this.editNode)
            return;
        const { data, name, error, isNew } = this.editNode;
        if (error || !this.formatName(name)) {
            this.cancelEdit();
            return;
        }
        data.name = this.formatName(name);
        this.editNode = null;
        // 如果是新增节点，需要在创建一个平级新增节点
        if (isNew) {
            const node = this.getNode(data.id);
            if (this.treeListData.length >= MAX_LAYER_NODE_LENGTH)
                return;
            this.createNode(node.parent ? node.parent.data : null);
        }
        this.treeData = [...this.treeData];
    }
    cancelEdit() {
        if (!this.editNode)
            return;
        const { data, isNew } = this.editNode;
        if (isNew) {
            const node = this.getNode(data.id);
            this.removeNode(node);
        }
        this.editNode = null;
        this.treeData = [...this.treeData];
    }
    createNode(parent) {
        const newNode = this.beforeCreateNode({
            id: uuidv4().replace(/-/g, ''),
            name: '',
        });
        if (this.treeListData.length >= MAX_LAYER_NODE_LENGTH) {
            Toast({
                type: 'error',
                message: this.i18n.t('snapBusiness.layerTree.maxCountError', { count: MAX_LAYER_NODE_LENGTH }),
                showClose: true,
            });
            return;
        }
        if (parent) {
            if (!parent.children) {
                Vue.set(parent, 'children', []);
            }
            parent.children.push(newNode);
            // 创建子节点，默认展开父节点
            this.treeRef.expandNode({ key: parent.id });
        }
        else {
            this.treeData.push(newNode);
        }
        this.treeData = [...this.treeData];
        this.startEdit(newNode, true);
    }
    focusNode(dataId) {
        Vue.nextTick(() => {
            const textareaElSelector = `textarea[data-el-id=G${dataId}][data-active=true]`;
            const isRendered = this.treeRef.$el.querySelector(textareaElSelector);
            const virtualSortTimer = 400; // 虚拟滚动组件排序重绘延迟时间为300ms
            if (isRendered) {
                const { y: y1 } = isRendered.getBoundingClientRect();
                const { y, height } = this.treeRef.$el.getBoundingClientRect();
                if (y1 < (y + height) && y1 >= y) {
                    setTimeout(() => {
                        requestAnimationFrame(() => {
                            isRendered.focus();
                        });
                    }, virtualSortTimer);
                    return;
                }
            }
            const index = _.findIndex(this.treeRef.flattenTree, { key: dataId });
            this.treeRef.$refs.virtualScrollerCompRef.scrollToItem(index + 1);
            setTimeout(() => {
                requestAnimationFrame(() => {
                    this.treeRef.$el.querySelector(textareaElSelector).focus();
                });
            }, virtualSortTimer);
        });
    }
    getSameName(nodes, excludeNodeData, name) {
        const names = _.compact(_.map(nodes, (data) => {
            if (data === excludeNodeData)
                return;
            return data.name;
        }));
        return names.includes(name);
    }
    validateNode(editNode) {
        const node = this.getNode(editNode.id);
        const name = this.formatName(editNode.name);
        const lowerCaseNameStr = name.toLowerCase();
        let errorMsg = '';
        let error = false;
        const isSameNodeError = this.getSameName(this.getParentChilds(node), node.data, name);
        if (!name) {
            error = true;
        }
        else if (isSameNodeError) {
            error = true;
            errorMsg = this.i18n.t('snapBusiness.layerTree.sameNodeNameError');
        }
        else if (!isValidSpecial(name) || _.some(SPECIAL_NODE_NAME_LIST, i => lowerCaseNameStr.indexOf(i) > -1)) {
            error = true;
            errorMsg = this.i18n.t('snapBusiness.layerTree.specialNodeNameError');
        }
        editNode.error = error;
        editNode.errorMsg = errorMsg;
        return editNode;
    }
    // 拖拽最后执行相关
    dragEnd(fromNode, toNode, dropType) {
        // 目标节点处已经存在同名节点
        const hasSameNameNode = dropType === 'inner'
            ? this.getSameName(toNode.data.children, null, fromNode.data.name)
            : this.getSameName(this.getParentChilds(toNode), fromNode.data, fromNode.data.name);
        if (hasSameNameNode) {
            Toast({
                type: 'error',
                showClose: true,
                message: this.i18n.t('snapBusiness.layerTree.sameNodeNameMoveError'),
            });
            return;
        }
        // 移动节点后超出最大层级限制
        let fromDeep = fromNode.level;
        forEachTree([fromNode], (data) => {
            fromDeep = Math.max(data.level, fromDeep);
        });
        let level = fromDeep - fromNode.level + 1 + toNode.level;
        if (dropType === 'before' || dropType === 'after') {
            level--;
        }
        if (level > this.maxTreeLevel) {
            Toast({
                type: 'error',
                message: this.i18n.t('snapBusiness.layerTree.maxLevelError', { level: this.maxTreeLevel }),
                showClose: true,
            });
            return false;
        }
        // 允许移动，开始移动数据
        // @ts-ignore
        Toast.closeAll();
        if (dropType === 'inner') {
            this.appendNode(fromNode, toNode);
            if (toNode.data.children?.length > 0) {
                this.treeRef.expandNode(toNode);
            }
        }
        if (dropType === 'before') {
            this.insertNodeBefore(fromNode, toNode);
        }
        if (dropType === 'after') {
            this.insertNodeAfter(fromNode, toNode);
        }
        this.treeData = [...this.treeData];
        console.info('[拖拽节点]: ', fromNode, dropType, toNode);
    }
    appendNode(fromNode, toNode) {
        this.removeNode(fromNode);
        if (!toNode.data.children) {
            Vue.set(toNode.data, 'children', []);
        }
        toNode.data.children.push(fromNode.data);
    }
    insertNodeBefore(fromNode, toNode) {
        this.removeNode(fromNode);
        const childs = this.getParentChilds(toNode);
        const index = _.findIndex(childs, { id: toNode.key });
        if (index > -1) {
            childs.splice(index, 0, fromNode.data);
        }
    }
    insertNodeAfter(fromNode, toNode) {
        this.removeNode(fromNode);
        const childs = this.getParentChilds(toNode);
        const index = _.findIndex(childs, { id: toNode.key });
        if (index > -1) {
            childs.splice(index + 1, 0, fromNode.data);
        }
    }
    removeNode(node) {
        const childs = this.getParentChilds(node);
        const index = _.findIndex(childs, { id: node.key });
        if (index > -1) {
            childs.splice(index, 1);
        }
        this.treeData = [...this.treeData];
    }
    getParentChilds(node) {
        let childs = this.treeData;
        if (node.parent) {
            childs = node.parent.data.children;
        }
        return childs;
    }
    ondragstart(event, node) {
        this.dragOption.dragNode = node;
        try {
            event.dataTransfer.setData('text/plain', '');
        }
        catch (e) {
            //
        }
        const target = event.target;
        const closestLayerItemEl = target.closest(`.${this.dragOption.dragHelperNodeClassName}`);
        const nodeEl = closestLayerItemEl?.cloneNode(true);
        nodeEl.style.width = `${closestLayerItemEl?.clientWidth}px`;
        nodeEl.style.backgroundColor = '#ebecf0';
        nodeEl.style.position = 'absolute';
        nodeEl.style.left = '-9999px';
        nodeEl.classList.add('helper');
        document.body.appendChild(nodeEl);
        document.body.style.overflow = 'hidden'; // Fix 火狐浏览器会造成外层出现滚动条
        event.dataTransfer.setDragImage(nodeEl, 12, 12);
        event.dataTransfer.effectAllowed = 'move';
    }
    ondragover(event, node, el) {
        event.dataTransfer.dropEffect = 'move';
        event.preventDefault();
        this.handleDragOver(event, node, el);
    }
    ondragleave(event, node) {
        this.dragOption.hoverType = null;
        this.dragOption.hoverElement = null;
    }
    ondrop(event, node) {
        event.preventDefault();
        const { dragNode, hoverNode, hoverType } = this.dragOption;
        if (hoverNode && hoverType) {
            this.dragEnd(dragNode, hoverNode, hoverType);
            this.dragOption.dragNode = null;
            this.dragOption.hoverNode = null;
        }
    }
    ondragend() {
        const helpers = document.querySelectorAll(`.helper.${this.dragOption.dragHelperNodeClassName}`);
        _.forEach(helpers, (helper) => {
            document.body.removeChild(helper);
            document.body.style.overflow = 'auto';
        });
        this.dragOption.dragNode = null;
        this.dragOption.hoverNode = null;
        this.dragOption.hoverType = null;
        this.dragOption.hoverElement = null;
    }
}
